import React, { useEffect, useState, useRef, useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { partnerService } from "../../services/partnerService";
import { transactionService } from "../../services/transactionService";
import { partnerMTLService } from "../../services/partnerMtlService";
import type { MTL } from "../../services/partnerMtlService";
import websocketService from "../../services/websocketService";

import { useTranslation } from "react-i18next";

import { MTLBenefitModalProps } from "../../types/MTLBenefitModalProps";

export const MTLBenefitModal: React.FC<MTLBenefitModalProps> = ({
  onClose,
}) => {
  // const { getConsent, confirmConsent, getBenefit } = usePartnerMTL();

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [cancelLabel, setCancelLabel] = useState("Cancel");
  const [confirmLabel, setConfirmLabel] = useState("Confirm");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isErrorHandling, setIsErrorHandling] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingIndicator, setLoadingIndicator] = useState("|");

  const loadingSymbols = ["|", "/", "-", "\\"];
  const symbolIndexRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const lang = i18n.language;

  const startLoadingIndicator = () => {
    intervalRef.current = setInterval(() => {
      symbolIndexRef.current =
        (symbolIndexRef.current + 1) % loadingSymbols.length;
      setLoadingIndicator(loadingSymbols[symbolIndexRef.current]);
    }, 250);
  };

  const stopLoadingIndicator = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isLoading) {
      startLoadingIndicator();
    } else {
      stopLoadingIndicator();
    }
    return stopLoadingIndicator;
  }, [isLoading]);

  const getConsentData = useCallback(async () => {
    const consent = await partnerMTLService.getConsent(lang);
    setIsButtonDisabled(true);

    if (consent) {
      setBodyContent(consent.consentLongText);
      setCancelLabel(lang === "th" ? "ไม่ยินยอม" : "I do not agree (refuse)");
      setConfirmLabel(lang === "th" ? "ยินยอม" : "I agree (consent)");
      setIsErrorHandling(false);
    } else {
      setBodyContent(
        lang === "th"
          ? "เกิดข้อผิดพลาดในการดึงข้อมูลการยินยอม กรุณาลองใหม่อีกครั้ง"
          : "There is an error retrieving consent. Please try again."
      );
      setCancelLabel(lang === "th" ? "ยกเลิก" : "Cancel");
      setConfirmLabel(lang === "th" ? "ลองใหม่" : "Retry");
      setIsErrorHandling(true);
    }

    setIsButtonDisabled(false);
  }, [lang]);

  useEffect(() => {
    setTitle(lang === "th" ? "ยินยอมตามข้อตกลง" : "Consent Agreement");
    getConsentData();
  }, [lang, getConsentData]);

  const handleCancelClick = async () => {
    if (isErrorHandling) {
      await transactionService.cancelQueue();
      navigate("/connect-failed");
    } else {
      await transactionService.cancelQueue();
      navigate("/connect-failed");
    }
  };

  const handleConfirmClick = async () => {
    setIsLoading(true);

    if (isErrorHandling) {
      await getConsentData();
    } else {
      const consentResult = await partnerMTLService.confirmConsent();

      // console.log("Consent result:", consentResult); // for debugging

      if (consentResult) {
        const partner = partnerService.getPartner() as MTL;
        // console.log("Partner object:", partner); // for debugging

        const {
          healthCardId,
          refCode: consentRefCode,
          clientId,
          userData,
        } = partner;
        const searchOption = userData!.searchOption;

        const benefitResult = await partnerMTLService.getBenefit(
          healthCardId,
          searchOption,
          consentRefCode,
          clientId
        );

        if (!benefitResult.success) {
          setBodyContent(benefitResult.errorMessage);
          setIsErrorHandling(true);
          setIsLoading(false);
          return;
        }
      }

      onClose();

      // Send dashboard notification after modal closes
      setTimeout(async () => {
        try {
          const partner = partnerService.getPartner() as MTL;
          const bu = partner?.hospitalId?.toString() || "1";
          await websocketService.sendDashboardNotification(
            bu,
            "patient_joined"
          );
        } catch (error) {
          console.log(
            "[MTL Modal] Error sending dashboard notification:",
            error
          );
        }
      }, 500);
    }

    setIsLoading(false);
  };

  return (
    <div className="flex items-center justify-center fixed inset-0 z-50 bg-gray-600 bg-opacity-50">
      <div
        className="w-full max-w-2xl border p-6 rounded-3xl shadow-md relative bg-white"
        role="document"
      >
        <div className="modal-content">
          <div className="modal-header text-center">
            <h4 id="modalHeader" className="modal-title">
              {title}
            </h4>
            <div className="border-b w-full border-gray-200 my-4" />
          </div>

          <div
            className="modal-body"
            style={{ height: "50vh", overflowY: "auto" }}
          >
            <div className="container-fluid">
              <p
                id="modalBodyContent"
                dangerouslySetInnerHTML={{ __html: bodyContent }}
              />
            </div>
          </div>

          <div className="border-t w-full border-gray-200 my-4" />
          <div className="modal-footer flex justify-end">
            {isLoading ? (
              <p>Please wait {loadingIndicator}</p>
            ) : (
              <div className="flex justify-between w-full">
                <button
                  id="modalCancelButton"
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelClick}
                  disabled={isButtonDisabled}
                >
                  {cancelLabel}
                </button>
                <button
                  id="modalConfirmButton"
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleConfirmClick}
                  disabled={isButtonDisabled}
                >
                  {confirmLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
